import React, { useMemo } from 'react';
import type { MedicalTimeline, TimelineEvent } from '../../api/documents';

interface MemberCriticalFindingsProps {
  timeline: MedicalTimeline | null;
  /** Click on a finding card → switch to the cronologie tab. The parent
   *  is responsible for applying the severity URL filter. */
  onOpenInTimeline: (event: TimelineEvent) => void;
}

function formatRoDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('ro-RO', { day: 'numeric', month: 'short', year: 'numeric' });
}

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="ov-empty-icon" aria-hidden>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const MemberCriticalFindings: React.FC<MemberCriticalFindingsProps> = ({
  timeline,
  onOpenInTimeline,
}) => {
  const findings = useMemo<TimelineEvent[]>(() => {
    const events = timeline?.events ?? [];
    // Severity ranking: critical first, then important. Within each
    // severity, the most recent date wins.
    const sevRank = (s: TimelineEvent['severity']) => (s === 'critical' ? 0 : s === 'important' ? 1 : 99);
    return events
      .filter((e) => e.severity === 'important' || e.severity === 'critical')
      .sort((a, b) => {
        const r = sevRank(a.severity) - sevRank(b.severity);
        if (r !== 0) return r;
        const ta = new Date(a.date).getTime();
        const tb = new Date(b.date).getTime();
        return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
      })
      .slice(0, 5);
  }, [timeline]);

  if (findings.length === 0) {
    return (
      <div className="ov-empty" role="status">
        <ShieldIcon />
        <div className="ov-empty-title">Nicio constatare critică</div>
        <div className="ov-empty-text">
          {timeline
            ? 'Cronologia nu conține evenimente importante sau critice.'
            : 'Generează cronologia pentru a identifica evenimente importante.'}
        </div>
      </div>
    );
  }

  return (
    <ul className="ov-findings">
      {findings.map((ev) => {
        const isCritical = ev.severity === 'critical';
        return (
          <li key={ev.id}>
            <button
              type="button"
              className={`ov-finding ${isCritical ? 'ov-finding-critical' : 'ov-finding-important'}`}
              onClick={() => onOpenInTimeline(ev)}
              style={{ width: '100%', textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit' }}
            >
              <div className="ov-finding-head">
                <span
                  className={`ov-finding-sev ${isCritical ? 'ov-finding-sev-critical' : 'ov-finding-sev-important'}`}
                >
                  {isCritical ? 'Critic' : 'Important'}
                </span>
                <span className="ov-finding-date">{formatRoDate(ev.date)}</span>
              </div>
              <h4 className="ov-finding-title">{ev.title}</h4>
              {ev.summary && <p className="ov-finding-summary">{ev.summary}</p>}
            </button>
          </li>
        );
      })}
    </ul>
  );
};

export default MemberCriticalFindings;
